/**
 * Field operators — combined registry + type-to-groups barrel.
 *
 * Sharded operator definitions in this directory are pulled together here:
 *
 *   - equality.ts     — equals, notEquals, equalsCI, notEqualsCI
 *   - numeric.ts      — greaterThan / lessThan family
 *   - date.ts         — before, after, onOrBefore, onOrAfter
 *   - text.ts         — contains, startsWith, endsWith, …
 *   - membership.ts   — includes, notIncludes
 *   - relationship.ts — isType, hasSchema, listedIn
 *   - array.ts        — isOneOf, isNotOneOf, hasAny, hasAll, hasNone
 *   - range.ts        — between, notBetween
 *   - presence.ts     — isEmpty, isNotEmpty, isTrue, isFalse
 *
 * Two roles coexist:
 *
 *   1. Persistence-boundary registry: every operator gets a `key`. Stored filter
 *      conditions reference operators by `key` (DB, URL params, GraphQL inputs).
 *      The flat `OPERATORS` map handles deserialization via `getOperatorByKey`.
 *
 *   2. Type-to-groups map: `OPERATOR_GROUPS` aggregates the per-kind operator-group
 *      maps under `./<kind>.ts` (value, reference, junction, computed) so the
 *      filter UI can ask `getOperatorGroups(type)` and get a typed list. Subtypes
 *      without an entry inherit from their parent via the type-chain walk.
 *
 * FieldType files (`../types/<name>.ts`) don't import from here — they're pure
 * data. The per-kind operator-group files own the wiring.
 *
 * This file and everything it imports must stay dependency-free (no Vue): the
 * operator definitions are consumed server-side.
 */

import type { FieldType, FieldTypeName, RootType } from "../types.js"
import { FIELD_TYPES } from "../types.js"
import { hasAll, hasAny, hasNone, isNotOneOf, isOneOf } from "./array.js"
import { operatorGroups as computedOperatorGroups } from "./computed.js"
import { after, before, onOrAfter, onOrBefore } from "./date.js"
import { equals, equalsCI, notEquals, notEqualsCI } from "./equality.js"
import { operatorGroups as junctionOperatorGroups } from "./junction.js"
import { includes, notIncludes } from "./membership.js"
import { greaterThan, greaterThanOrEqual, lessThan, lessThanOrEqual } from "./numeric.js"
import { isEmpty, isFalse, isNotEmpty, isTrue } from "./presence.js"
import { between, notBetween } from "./range.js"
import { operatorGroups as referenceOperatorGroups } from "./reference.js"
import { hasSchema, isType, listedIn } from "./relationship.js"
import { contains, endsWith, notContains, startsWith } from "./text.js"
import { operatorGroups as valueOperatorGroups } from "./value.js"

// TYPES =======================================================================

export type OperatorValueShape = "none" | "scalar" | "array" | "range"

export interface Operator {
  /** Persistence-boundary string id. Matches the export name in most cases. */
  key: string
  label: string
  icon: string
  value: OperatorValueShape
  fn: (a: unknown, b?: unknown) => boolean
}

export interface OperatorGroup {
  label: string
  operators: Operator[]
}

// OPERATOR RE-EXPORTS =========================================================

export {
  // equality
  equals,
  notEquals,
  equalsCI,
  notEqualsCI,
  // numeric
  greaterThan,
  greaterThanOrEqual,
  lessThan,
  lessThanOrEqual,
  // date
  before,
  after,
  onOrBefore,
  onOrAfter,
  // text
  contains,
  notContains,
  startsWith,
  endsWith,
  // membership
  includes,
  notIncludes,
  // relationship
  isType,
  hasSchema,
  listedIn,
  // array
  isOneOf,
  isNotOneOf,
  hasAny,
  hasAll,
  hasNone,
  // range
  between,
  notBetween,
  // presence
  isEmpty,
  isNotEmpty,
  isTrue,
  isFalse,
}

// PERSISTENCE-BOUNDARY LOOKUP =================================================

const ALL_OPERATORS: readonly Operator[] = [
  equals,
  notEquals,
  equalsCI,
  notEqualsCI,
  greaterThan,
  greaterThanOrEqual,
  lessThan,
  lessThanOrEqual,
  before,
  after,
  onOrBefore,
  onOrAfter,
  contains,
  notContains,
  startsWith,
  endsWith,
  includes,
  notIncludes,
  isType,
  hasSchema,
  listedIn,
  isOneOf,
  isNotOneOf,
  hasAny,
  hasAll,
  hasNone,
  between,
  notBetween,
  isEmpty,
  isNotEmpty,
  isTrue,
  isFalse,
]

/** Flat key→operator map for hydrating stored filter conditions. */
export const OPERATORS: Record<string, Operator> = Object.fromEntries(
  ALL_OPERATORS.map((op) => [op.key, op])
)

export function getOperatorByKey(key: string): Operator | null {
  return OPERATORS[key] ?? null
}

// TYPE-TO-GROUPS MAP ==========================================================

export const OPERATOR_GROUPS: Partial<Record<RootType | FieldTypeName, OperatorGroup[]>> = {
  ...valueOperatorGroups,
  ...referenceOperatorGroups,
  ...junctionOperatorGroups,
  ...computedOperatorGroups,
}

/**
 * Walk the type chain leaf → root and return the first registered operator
 * groups. Subtypes that don't declare `operators` fall through to the parent
 * type's groups.
 */
export function getOperatorGroups(type: string): OperatorGroup[] {
  let current: FieldType | undefined = FIELD_TYPES[type]
  while (current) {
    const entry = OPERATOR_GROUPS[current.name]
    if (entry) return entry
    if (!current.parent) break
    current = FIELD_TYPES[current.parent]
  }
  return OPERATOR_GROUPS.string ?? []
}

/** Flat list of operator keys for a field type. Used at the persistence boundary
 *  (validation, stored-filter compatibility checks). */
export function getOperatorsForType(type: string): string[] {
  return getOperatorGroups(type).flatMap((g) => g.operators.map((op: Operator) => op.key))
}

/** Flat list of operator objects for a field type. */
export function getOperatorObjectsForType(type: string): Operator[] {
  return getOperatorGroups(type).flatMap((g) => g.operators)
}
